/**
 * A Link: what posts one entry into another record. Section 5.7, decision D10.
 *
 * `app.link` has held these columns since migration 0001 and nothing read
 * them. This file is the shape those jsonb columns take, plus the evaluator
 * that turns a source entry into a derived one.
 *
 * The evaluator lives HERE and not in apps/server on purpose. Section 5.7
 * rule 6: "The device runs the same rule." One implementation, imported by the
 * write path and by the mirror, is the only way that promise can hold.
 *
 * Two modes ship: `auto` fires silently, `paused` skips the Link and keeps
 * every row already written. `draft` exists in the database enum and is
 * deliberately not offered: approving a derived row needs the status ladder of
 * Section 5.13, which has no screen.
 *
 * Only `feed` has an engine (`LINK_PATTERN_ENGINE`). The other four patterns
 * are accepted, stored and listed, and they write nothing: `settle` needs the open-row matcher,
 * `rollup` and `balance` are reads by definition, and `reminder` needs
 * `data.task`.
 */

import {
  evaluateFormula,
  formulaFieldsRead,
  formulaHopsRead,
  readFormulaSpec,
  type FormulaSpec,
  type FormulaValueType,
} from './formula';
import type { CoversAll } from './covers-all';

/* ------------------------------------------------------------------ the rule */

/** What a Link does when it fires. The app.link_pattern enum, exactly. */
export type LinkPattern = 'feed' | 'settle' | 'rollup' | 'balance' | 'reminder';

/** Every pattern, in the order of the type. */
export const LINK_PATTERNS = ['feed', 'settle', 'rollup', 'balance', 'reminder'] as const satisfies readonly LinkPattern[];
true satisfies CoversAll<LinkPattern, typeof LINK_PATTERNS>;

/**
 * Which patterns write anything. Only `feed`: the rest are stored and listed
 * and write nothing (the header says what each one waits for).
 */
export const LINK_PATTERN_ENGINE: Readonly<Record<LinkPattern, boolean>> = {
  feed: true,
  settle: false,
  rollup: false,
  balance: false,
  reminder: false,
};

/** The two modes with an engine behind them. `draft` is not offered yet. */
export type LinkMode = 'auto' | 'paused';

export const LINK_MODES = ['auto', 'paused'] as const satisfies readonly LinkMode[];
true satisfies CoversAll<LinkMode, typeof LINK_MODES>;

/**
 * One comparison inside a trigger.
 *
 * `field` names a field on the SOURCE record. `op` is closed, so a trigger is
 * never an expression anybody has to parse. `values` holds the alternatives
 * for that one field, and any one of them is enough: that is the OR a person
 * means when they say "cash or online".
 */
export type LinkCondition = {
  fieldId: string;
  op: LinkConditionOp;
  /** Empty on `filled` and `empty`. One entry only on a numeric op. */
  values: readonly (string | number)[];
  /**
   * A reference field on the SOURCE, when the question is asked of the row it
   * points at: "only when the customer's Payment terms are Credit".
   *
   * With `via` set, `fieldId` names a field on that other record instead of on
   * the source. Depth stays one hop, the same rule a formula keeps, and the
   * evaluator needs a reader to answer at all — without one the condition
   * holds nothing rather than guessing.
   */
  via?: string;
};

export type LinkConditionOp =
  | 'is'
  | 'is_not'
  | 'filled'
  | 'empty'
  | 'at_least'
  | 'at_most'
  | 'exactly'
  | 'contains'
  | 'not_contains';

/** Every condition op, in the order of the type. */
export const LINK_CONDITION_OPS = [
  'is',
  'is_not',
  'filled',
  'empty',
  'at_least',
  'at_most',
  'exactly',
  'contains',
  'not_contains',
] as const satisfies readonly LinkConditionOp[];
true satisfies CoversAll<LinkConditionOp, typeof LINK_CONDITION_OPS>;

/** The ops that need no value at all. */
const VALUELESS: ReadonlySet<LinkConditionOp> = new Set(['filled', 'empty']);

/** The ops that compare numbers, and so hold exactly one value. */
const NUMERIC: ReadonlySet<LinkConditionOp> = new Set(['at_least', 'at_most', 'exactly']);

export const opNeedsValue = (op: LinkConditionOp): boolean => !VALUELESS.has(op);
export const opIsNumeric = (op: LinkConditionOp): boolean => NUMERIC.has(op);

/**
 * One group of conditions. Any one of them is enough.
 *
 * A group with a single condition is the common case, and a condition holding
 * several values is the second most common. A group with several conditions on
 * DIFFERENT fields is the cross-field OR.
 */
export type LinkConditionGroup = { any: readonly LinkCondition[] };

/**
 * When a Link fires: an AND of ORs, two levels deep and never deeper.
 *
 * `all` is joined by AND. Each group's `any` is joined by OR. Every condition
 * a business can state fits this shape, because any boolean expression can be
 * written as an AND of ORs. So there are no brackets, no precedence, and the
 * evaluator is two nested loops rather than a parser.
 *
 * An empty `all` means always. That is the `{}` the column defaults to.
 */
export type LinkTrigger = { all: readonly LinkConditionGroup[] };

/** What fills one field on the target. */
export type LinkMapping =
  /** Copy a field off the source entry. */
  | { kind: 'field'; fieldId: string }
  /** The same value on every row a Link writes. */
  | { kind: 'const'; value: string | number | boolean }
  /**
   * Read a value off the row a source reference points at: the item's price,
   * the vehicle's driver. `via` is a reference field on the source, `fieldId`
   * a field on the record it points at.
   *
   * `app.field.default_expr` already carries {from_reference, field}, so this
   * is that existing shape reused rather than a new idea.
   */
  | { kind: 'reference'; via: string; fieldId: string }
  /**
   * A value worked out from the source row: `(litres, or else the customer's
   * usual) × (rate, or else the customer's usual)`.
   *
   * The same tree a formula field holds, run by the same evaluator, over the
   * source entry's values. So a posting is worked out once and read the same
   * way everywhere, rather than needing a second grammar for links.
   *
   * The answer is written only when it is not null. A formula that cannot be
   * worked out leaves the target field empty, which the target's own required
   * check then judges — a zero would be a number somebody's total swallows.
   */
  | { kind: 'formula'; spec: FormulaSpec };

/** Every way a target field is filled, in the order of the type. */
export const LINK_MAPPING_KINDS = ['field', 'const', 'reference', 'formula'] as const satisfies readonly LinkMapping['kind'][];
true satisfies CoversAll<LinkMapping['kind'], typeof LINK_MAPPING_KINDS>;

/** Target field id to what fills it. A field with no entry is left empty. */
export type LinkFieldMap = Readonly<Record<string, LinkMapping>>;

/** Where a feed lands. `match_open_row` has no engine yet. */
export type LinkTarget =
  | { mode: 'create_row' }
  | { mode: 'match_open_row'; byFieldId: string; whereStatus: string };

/** One Link, as every screen and both engines read it. */
export type LinkSpec = {
  id: string;
  fromRecordId: string;
  toRecordId: string;
  pattern: LinkPattern;
  ord: number;
  trigger: LinkTrigger;
  fieldMap: LinkFieldMap;
  target: LinkTarget;
  sign: 1 | -1;
  mode: LinkMode;
  version: number;
};

/* ---------------------------------------------------------- reading it back */

/** One Link as the relationship screen reads it: the rule, plus its receipts. */
export type LinkListItem = LinkSpec & {
  /** The tenant's own name for the target record, so no screen holds a uuid. */
  toRecordName: string;
  fromRecordName: string;
  /** Raised on every firing. The trust surface. */
  fireCount: number;
  /** user, template, onboarding, or suggestion:<id>. */
  createdBy: string;
  createdAt: string;
};

/** GET /v1/records/:recordId/links */
export type LinkListResponse = {
  /** The rules THIS record fires: every live link out of it, paused ones included. */
  links: LinkListItem[];
  /**
   * The rules that post INTO this record, from other records. Read-only here:
   * a rule belongs to the record that fires it, so pause and edit live on the
   * source. Listed so a record that receives rows says where they come from,
   * instead of showing "Nothing is linked yet" beside a row wearing Auto.
   */
  incoming: LinkListItem[];
  /** Records this one may post into: same tenant, not itself, not a directory. */
  targets: Array<{ recordId: string; name: string }>;
};

/** POST /v1/records/:recordId/links */
export type LinkCreateRequest = {
  toRecordId: string;
  pattern: LinkPattern;
  trigger?: LinkTrigger;
  fieldMap: LinkFieldMap;
  target?: LinkTarget;
  sign?: 1 | -1;
  mode?: LinkMode;
  ord?: number;
};

/** PATCH /v1/records/:recordId/links/:linkId. Every field optional. */
export type LinkPatchRequest = Partial<Omit<LinkCreateRequest, 'toRecordId' | 'pattern'>>;

export type LinkWriteResponse = { link: LinkListItem };

/** What a refused Link says. Every problem at once, never first-problem-wins. */
export type LinkProblemCode =
  | 'TARGET_SAME_RECORD'
  | 'TARGET_UNKNOWN'
  | 'CYCLE'
  | 'FIELD_UNKNOWN'
  | 'TYPE_MISMATCH'
  | 'REQUIRED_UNFILLED'
  | 'TRIGGER_SHAPE'
  | 'PATTERN_UNSUPPORTED'
  | 'TARGET_MODE_UNSUPPORTED'
  /** A link starts from, or lands in, a lines record. Lines are written only inside their entry. */
  | 'LINES_RECORD';

export type LinkProblem = { code: LinkProblemCode; fieldId?: string; message: string };

/* ------------------------------------------------------------- the evaluator */

/** A field-id keyed entry, the shape both sides already pass around. */
export type LinkValues = Readonly<Record<string, unknown>>;

/** True when the value is present in the sense `filled` means. */
const isFilled = (value: unknown): boolean =>
  value !== null && value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0);

const asText = (value: unknown): string => (value === null || value === undefined ? '' : String(value));

/**
 * The value a condition is asking about: on this entry, or on the row it
 * points at. Undefined when the hop cannot be made, so `filled` says no and
 * `empty` says yes, which is the honest reading of a reference nobody set.
 */
const askedValue = (
  condition: LinkCondition,
  values: LinkValues,
  readReference: ReferenceReader | null,
): unknown => {
  if (condition.via === undefined) return values[condition.fieldId];
  const pointed = values[condition.via];
  if (typeof pointed !== 'string' || pointed === '' || readReference === null) return undefined;
  return readReference(pointed, condition.fieldId);
};

/**
 * One condition against one entry. Unknown ops refuse rather than pass.
 *
 * `readReference` is only ever used by a condition with a `via`, and passing
 * null refuses every one of them — which is what a preview with nothing loaded
 * should do, and what every caller written before hop conditions existed does
 * by leaving the argument off.
 */
export const conditionHolds = (
  condition: LinkCondition,
  values: LinkValues,
  readReference: ReferenceReader | null = null,
): boolean => {
  const held = askedValue(condition, values, readReference);

  if (condition.op === 'filled') return isFilled(held);
  if (condition.op === 'empty') return !isFilled(held);
  if (condition.values.length === 0) return false;

  if (condition.op === 'is' || condition.op === 'is_not') {
    // A multi-value field holds a list, so membership is the test either way.
    const carried = Array.isArray(held) ? held.map(asText) : [asText(held)];
    const wanted = condition.values.map(asText);
    const found = carried.some((one) => wanted.includes(one));
    return condition.op === 'is' ? found : !found;
  }

  if (opIsNumeric(condition.op)) {
    const left = Number(held);
    const right = Number(condition.values[0]);
    if (!Number.isFinite(left) || !Number.isFinite(right)) return false;
    if (condition.op === 'at_least') return left >= right;
    if (condition.op === 'at_most') return left <= right;
    return left === right;
  }

  const text = asText(held).toLowerCase();
  const found = condition.values.some((one) => text.includes(asText(one).toLowerCase()));
  return condition.op === 'contains' ? found : !found;
};

/**
 * Whether a Link fires for this entry.
 *
 * Two nested loops, in this order: every group must hold, and a group holds
 * when any one of its conditions holds. An empty `all` means always, which is
 * what the column's `{}` default says.
 */
export const triggerHolds = (
  trigger: LinkTrigger,
  values: LinkValues,
  readReference: ReferenceReader | null = null,
): boolean => {
  if (trigger.all.length === 0) return true;
  return trigger.all.every(
    (group) =>
      group.any.length > 0 &&
      group.any.some((condition) => conditionHolds(condition, values, readReference)),
  );
};

/** How a `reference` mapping reads the row a source reference points at. */
export type ReferenceReader = (entryId: string, fieldId: string) => unknown;

/**
 * The values a feed writes into the target, before the target validates them.
 *
 * The sign is applied to the target's measure by the caller, not here: only
 * the write path knows which target field the measure binding names.
 */
export const derivedValues = (
  map: LinkFieldMap,
  source: LinkValues,
  readReference: ReferenceReader | null = null,
): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [targetFieldId, mapping] of Object.entries(map)) {
    if (mapping.kind === 'field') {
      const held = source[mapping.fieldId];
      if (held !== undefined) out[targetFieldId] = held;
      continue;
    }
    if (mapping.kind === 'const') {
      out[targetFieldId] = mapping.value;
      continue;
    }
    if (mapping.kind === 'formula') {
      // Null is "nothing", never zero, so a formula that cannot be worked out
      // writes nothing at all rather than a figure nobody meant.
      const answer = evaluateFormula(mapping.spec, source, readReference);
      if (answer !== null) out[targetFieldId] = answer;
      continue;
    }
    const pointed = source[mapping.via];
    if (typeof pointed !== 'string' || readReference === null) continue;
    const read = readReference(pointed, mapping.fieldId);
    if (read !== undefined) out[targetFieldId] = read;
  }
  return out;
};

/* ------------------------------------------------------------- the type table */

/**
 * What may fill what. The closed table IS the design: one renderer, one
 * validator, and no expression anywhere for anybody to parse.
 *
 * A key is the TARGET field's type; the list is the source types that fit it.
 * `money` takes only money on purpose — a quantity is not an amount, and a link
 * that quietly treated one as the other would be the worst kind of bug.
 *
 * It lives here rather than in the route because the field map screen must grey
 * out exactly what the server would refuse. Two copies of this table would be
 * two products.
 */
const FITS: Readonly<Record<string, readonly string[]>> = {
  text: ['text'],
  long_text: ['long_text', 'text'],
  number: ['number', 'quantity'],
  money: ['money'],
  quantity: ['quantity', 'number'],
  date: ['date', 'datetime'],
  datetime: ['datetime', 'date'],
  boolean: ['boolean'],
  select: ['select'],
  multi_select: ['multi_select', 'select'],
  reference: ['reference'],
  attachment: ['attachment'],
  phone: ['phone', 'text'],
  location: ['location'],
};

/**
 * Whether a source field of one type may fill a target field of another.
 *
 * A computed target is never fillable: nothing stores it. That is checked by
 * the caller, which knows the field's storage; this function knows only types.
 */
export const mappingTypeOk = (targetType: string, sourceType: string): boolean =>
  (FITS[targetType] ?? []).includes(sourceType);

/** Every source type that could fill this target type, for a picker to offer. */
export const sourceTypesFor = (targetType: string): readonly string[] => FITS[targetType] ?? [];

/**
 * What a formula's answered type is called in the table above.
 *
 * The two vocabularies are the same six words today, and this function is what
 * makes that a fact rather than a coincidence: a worked-out mapping is fitted
 * to its target by `mappingTypeOk(targetType, formulaValueTypeAsFieldType(answered))`,
 * so a count can never land in an amount. Written out rather than passed
 * through, so the day one side gains a word the other has to answer for it.
 */
const VALUE_TYPE_AS_FIELD_TYPE: Readonly<Record<FormulaValueType, string>> = {
  number: 'number',
  money: 'money',
  quantity: 'quantity',
  text: 'text',
  boolean: 'boolean',
  date: 'date',
};

export const formulaValueTypeAsFieldType = (type: FormulaValueType): string =>
  VALUE_TYPE_AS_FIELD_TYPE[type];

/* --------------------------------------------------------------- validation */

/** The trigger shape, checked without trusting the caller. Depth is fixed at two. */
export const readTrigger = (raw: unknown): LinkTrigger | null => {
  if (raw === null || raw === undefined) return { all: [] };
  if (typeof raw !== 'object' || Array.isArray(raw)) return null;
  const held = (raw as { all?: unknown }).all;
  // `{}` is the column default and means always.
  if (held === undefined) return { all: [] };
  if (!Array.isArray(held)) return null;

  const groups: LinkConditionGroup[] = [];
  for (const rawGroup of held) {
    if (typeof rawGroup !== 'object' || rawGroup === null) return null;
    const any = (rawGroup as { any?: unknown }).any;
    if (!Array.isArray(any) || any.length === 0) return null;

    const conditions: LinkCondition[] = [];
    for (const rawCondition of any) {
      const read = readCondition(rawCondition);
      if (read === null) return null;
      conditions.push(read);
    }
    groups.push({ any: conditions });
  }
  return { all: groups };
};

const OPS: ReadonlySet<string> = new Set([
  'is',
  'is_not',
  'filled',
  'empty',
  'at_least',
  'at_most',
  'exactly',
  'contains',
  'not_contains',
]);

const readCondition = (raw: unknown): LinkCondition | null => {
  if (typeof raw !== 'object' || raw === null) return null;
  const held = raw as { fieldId?: unknown; op?: unknown; values?: unknown; via?: unknown };
  if (typeof held.fieldId !== 'string' || held.fieldId === '') return null;
  if (typeof held.op !== 'string' || !OPS.has(held.op)) return null;
  const op = held.op as LinkConditionOp;

  // Absent is the ordinary condition. Present and empty is a half-built hop,
  // which would silently read the source's own field instead.
  if (held.via !== undefined && (typeof held.via !== 'string' || held.via === '')) return null;
  const hop = held.via === undefined ? {} : { via: held.via as string };

  const rawValues = held.values ?? [];
  if (!Array.isArray(rawValues)) return null;
  const values: (string | number)[] = [];
  for (const one of rawValues) {
    if (typeof one !== 'string' && typeof one !== 'number') return null;
    values.push(one);
  }
  if (!opNeedsValue(op)) return { fieldId: held.fieldId, op, values: [], ...hop };
  if (values.length === 0) return null;
  // A range is two conditions, never one, so a numeric op holds one figure.
  if (opIsNumeric(op) && values.length !== 1) return null;
  return { fieldId: held.fieldId, op, values, ...hop };
};

/** The field map shape, checked the same way. */
export const readFieldMap = (raw: unknown): LinkFieldMap | null => {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const out: Record<string, LinkMapping> = {};
  for (const [targetFieldId, rawMapping] of Object.entries(raw)) {
    if (typeof rawMapping !== 'object' || rawMapping === null) return null;
    const held = rawMapping as {
      kind?: unknown;
      fieldId?: unknown;
      value?: unknown;
      via?: unknown;
      spec?: unknown;
    };
    if (held.kind === 'field') {
      if (typeof held.fieldId !== 'string' || held.fieldId === '') return null;
      out[targetFieldId] = { kind: 'field', fieldId: held.fieldId };
      continue;
    }
    if (held.kind === 'const') {
      const value = held.value;
      if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') return null;
      out[targetFieldId] = { kind: 'const', value };
      continue;
    }
    if (held.kind === 'reference') {
      if (typeof held.via !== 'string' || held.via === '') return null;
      if (typeof held.fieldId !== 'string' || held.fieldId === '') return null;
      out[targetFieldId] = { kind: 'reference', via: held.via, fieldId: held.fieldId };
      continue;
    }
    if (held.kind === 'formula') {
      // One reader for the tree, wherever it is stored. A mapping nobody can
      // read takes the whole map down rather than posting a wrong figure.
      const spec = readFormulaSpec(held.spec);
      if (spec === null) return null;
      out[targetFieldId] = { kind: 'formula', spec };
      continue;
    }
    return null;
  }
  return out;
};

/** The target shape. `match_open_row` parses but has no engine yet. */
export const readTarget = (raw: unknown): LinkTarget | null => {
  if (raw === null || raw === undefined) return { mode: 'create_row' };
  if (typeof raw !== 'object' || Array.isArray(raw)) return null;
  const held = raw as { mode?: unknown; byFieldId?: unknown; whereStatus?: unknown };
  if (held.mode === undefined || held.mode === 'create_row') return { mode: 'create_row' };
  if (held.mode !== 'match_open_row') return null;
  if (typeof held.byFieldId !== 'string' || held.byFieldId === '') return null;
  const whereStatus = typeof held.whereStatus === 'string' ? held.whereStatus : 'open';
  return { mode: 'match_open_row', byFieldId: held.byFieldId, whereStatus };
};

/** Every field on the source that a Link reads. Used by the cycle and type checks. */
export const sourceFieldsRead = (spec: {
  trigger: LinkTrigger;
  fieldMap: LinkFieldMap;
}): readonly string[] => {
  const out = new Set<string>();
  for (const group of spec.trigger.all) {
    // A hop condition reads the reference and nothing else on the source: its
    // `fieldId` names a field on the OTHER record.
    for (const condition of group.any) out.add(condition.via ?? condition.fieldId);
  }
  for (const mapping of Object.values(spec.fieldMap)) {
    if (mapping.kind === 'field') out.add(mapping.fieldId);
    if (mapping.kind === 'reference') out.add(mapping.via);
    // `formulaFieldsRead` already answers the `via` of every hop inside it.
    if (mapping.kind === 'formula') for (const fieldId of formulaFieldsRead(mapping.spec)) out.add(fieldId);
  }
  return [...out];
};

/**
 * Every pair the Link reads through a reference: the reference field on the
 * source, and the field it reads on the row that points at.
 *
 * The server loads these up front, one query per pair, so `triggerHolds` and
 * `derivedValues` stay synchronous and shared with the device. It is the
 * `loadReferenceValues` shopping list, and it now has three sources: a
 * reference mapping, a hop inside a worked-out mapping, and a hop condition.
 * De-duplicated, because two of them naming the same pair is the common case.
 */
export const linkHopsRead = (spec: {
  trigger: LinkTrigger;
  fieldMap: LinkFieldMap;
}): readonly { via: string; fieldId: string }[] => {
  const seen = new Set<string>();
  const out: { via: string; fieldId: string }[] = [];
  const add = (via: string, fieldId: string): void => {
    const key = `${via} ${fieldId}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ via, fieldId });
  };

  for (const mapping of Object.values(spec.fieldMap)) {
    if (mapping.kind === 'reference') add(mapping.via, mapping.fieldId);
    if (mapping.kind === 'formula') {
      for (const hop of formulaHopsRead(mapping.spec)) add(hop.via, hop.fieldId);
    }
  }
  for (const group of spec.trigger.all) {
    for (const condition of group.any) {
      if (condition.via !== undefined) add(condition.via, condition.fieldId);
    }
  }
  return out;
};

/** The write budget of Section 5.7: a save fires at most this many Links. */
export const LINK_FIRE_CAP = 8;
